import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';

/**
 * HTML conversion uses Turndown + its GFM table rules. DOMParser only parses
 * an inert string: the source is never inserted into the live document and no
 * URL is fetched. The sanitizer removes executable/resource-bearing elements
 * before Turndown sees the fragment.
 */
export const HTML_MAX_BYTES = 5 * 1024 * 1024;
export const HTML_MAX_OUTPUT = 4 * 1024 * 1024;

const DROP_TAGS = 'head,title,script,style,noscript,template,iframe,object,embed,link,meta,base,svg,canvas,video,audio,source';
const SAFE_HREF = /^(?:https?:\/\/|mailto:|tel:|#|\/(?!\/)|\.\.?\/)/i;
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

function abortIfNeeded(signal) {
  if (signal?.aborted) throw new DOMException('Conversion canceled.', 'AbortError');
}

function sanitizer(source, { keepLinks = true, stripEmpty = true } = {}) {
  if (typeof DOMParser === 'undefined') throw new Error('HTML conversion requires a browser DOMParser.');
  const document = globalThis.document;
  if (!document?.createElement) throw new Error('HTML conversion requires a browser document.');
  // A template's DocumentFragment is inert and is never attached to the live
  // document. This keeps image, iframe, object, link and srcset parsing from
  // initiating resource requests while retaining HTML parsing behaviour.
  const template = document.createElement('template');
  template.innerHTML = source;
  const fragment = template.content;
  let imageCount = 0;
  fragment.querySelectorAll(DROP_TAGS).forEach((node) => node.remove());
  fragment.querySelectorAll('img').forEach((node) => {
    imageCount += 1;
    const alt = node.getAttribute('alt')?.trim();
    node.replaceWith(alt ? template.ownerDocument.createTextNode(`[Image: ${alt}]`) : template.ownerDocument.createTextNode(''));
  });
  fragment.querySelectorAll('*').forEach((node) => {
    [...node.attributes].forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      if (name.startsWith('on') || name === 'src' || name === 'srcset' || name === 'style') node.removeAttribute(attribute.name);
      if (name === 'href' && (!keepLinks || !SAFE_HREF.test(attribute.value.trim()) || CONTROL_CHARS.test(attribute.value))) node.removeAttribute(attribute.name);
    });
  });
  if (stripEmpty) {
    // Keep table rows and cells even when a cell is empty; removing an empty
    // <td> changes the column positions in Turndown's GFM table output.
    const structural = 'address,article,aside,blockquote,dd,div,dl,dt,fieldset,figcaption,figure,footer,form,header,li,main,nav,ol,p,section,ul';
    [...fragment.querySelectorAll(structural)].reverse().forEach((node) => {
      if (!node.textContent?.trim() && !node.querySelector('img,br')) node.remove();
    });
  }
  return { html: template.innerHTML, imageCount };
}

function converter() {
  const service = new TurndownService({
    headingStyle: 'atx',
    bulletListMarker: '-',
    codeBlockStyle: 'fenced',
    emDelimiter: '*',
    strongDelimiter: '**',
  });
  service.use(gfm);
  return service;
}

export function convertHtmlToMarkdown(source, {
  signal,
  sourceMode = 'fragment',
  keepLinks = true,
  stripEmpty = true,
} = {}) {
  const text = String(source ?? '');
  if (!text.trim()) throw new Error('Paste HTML or choose an HTML file first.');
  if (new TextEncoder().encode(text).byteLength > HTML_MAX_BYTES) throw new Error('The HTML source is larger than 5 MB.');
  if (!['fragment', 'document'].includes(sourceMode)) throw new Error('HTML source mode must be fragment or document.');
  abortIfNeeded(signal);
  const { html, imageCount } = sanitizer(text, { keepLinks, stripEmpty });
  abortIfNeeded(signal);
  const markdown = converter().turndown(html).replace(/\n{3,}/g, '\n\n').trim();
  if (new TextEncoder().encode(markdown).byteLength > HTML_MAX_OUTPUT) throw new Error('The generated Markdown is larger than 4 MB.');
  const warnings = [];
  if (imageCount) warnings.push(`${imageCount} image${imageCount === 1 ? '' : 's'} omitted; remote resources are never fetched.`);
  if (!markdown) warnings.push('No readable HTML content was found.');
  return { markdown, warnings, assets: [] };
}

export function validateHtmlText(source) {
  const text = String(source ?? '');
  if (!text.trim()) return 'Paste HTML or choose an HTML file first.';
  if (new TextEncoder().encode(text).byteLength > HTML_MAX_BYTES) return 'The HTML source is larger than 5 MB.';
  return '';
}
